import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { ProductImage } from "../../components/ProductImage";
import { toast } from "sonner";
import {
  Search,
  SlidersHorizontal,
  ChevronRight,
  ChevronDown,
  X,
  Flame,
  TrendingDown,
  AlertTriangle,
  Box,
  Receipt,
  ArrowUpDown,
} from "lucide-react";
import RemovalsFilterSheet, { EMPTY_FILTERS } from "../employee/RemovalsFilterSheet";
import { makeOperatorResolver, operatorName } from "../../lib/operatorName";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import EmptyState from "../../components/system/EmptyState";
import StatusChip from "../../components/system/StatusChip";

/**
 * Admin / Manager Removal Log — full historical audit view.
 *
 * Date-bucketed (Today / Yesterday / Last 7 days / Older) with sort,
 * search, filter, and a 5-tile summary. Same `/removals` endpoint as
 * before; backend already scopes results by the caller's role
 * (admin sees all stores in the company, manager sees their store).
 *
 * Employees are kept off this surface by the App.js role guard; their
 * `/removals` route now points at the focused Action Items view.
 */

const REASON_LABEL = {
  expired: "Expired",
  near_expiration: "Near expiration",
  damaged: "Damaged",
  sold_out: "Used / sold",
  other: "Other",
};

const REASON_TONE = {
  expired: "expired",
  near_expiration: "urgent",
  damaged: "neutral",
  sold_out: "neutral",
  other: "neutral",
};

function formatTimeShort(iso) {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}
function formatDateTime(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
function operatorLabel(opId) {
  return operatorName(null, opId);
}

/* ---------------------- filtering --------------------------------------- */

function presetCutoff(preset) {
  const now = new Date();
  if (preset === "today") {
    const start = new Date(now); start.setHours(0, 0, 0, 0);
    return { from: start, to: null };
  }
  if (preset === "7d")  { const f = new Date(now); f.setDate(f.getDate() - 7);  return { from: f, to: null }; }
  if (preset === "30d") { const f = new Date(now); f.setDate(f.getDate() - 30); return { from: f, to: null }; }
  return { from: null, to: null };
}

function applyFilters(rows, f) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const needle = (f.query || "").trim().toLowerCase();
  let from = null, to = null;
  if (f.datePreset === "custom") {
    from = f.customFrom ? new Date(`${f.customFrom}T00:00:00`) : null;
    to   = f.customTo   ? new Date(`${f.customTo}T23:59:59.999`) : null;
  } else {
    const r = presetCutoff(f.datePreset);
    from = r.from; to = r.to;
  }
  const reasonSet = f.reasons.length ? new Set(f.reasons) : null;
  const opSet     = f.operators.length ? new Set(f.operators) : null;
  return rows.filter((r) => {
    if (needle) {
      const reasonLabel = REASON_LABEL[r.reason] || r.reason || "";
      const haystack = [r.product_name, r.product_barcode, r.product_id, reasonLabel, r.note, formatDateTime(r.removed_at)]
        .filter(Boolean).map((s) => String(s).toLowerCase()).join(" ");
      if (!haystack.includes(needle)) return false;
    }
    if (from || to) {
      const t = new Date(r.removed_at);
      if (from && t < from) return false;
      if (to && t > to)     return false;
    }
    if (reasonSet) {
      const rk = r.reason in REASON_LABEL ? r.reason : "other";
      if (!reasonSet.has(rk)) return false;
    }
    if (opSet && !opSet.has(r.removed_by || "")) return false;
    return true;
  });
}

function buildProductGroups(rows) {
  const map = new Map();
  for (const r of rows) {
    const key = r.product_id || r.product_barcode || r.id;
    let g = map.get(key);
    if (!g) {
      g = {
        key, product_id: r.product_id,
        product_name: r.product_name || r.product_id || "—",
        product_barcode: r.product_barcode || null,
        product_image_url: r.product_image_url || null,
        totalQuantity: 0, entryCount: 0,
        lastRemovedAt: r.removed_at,
        reasonSet: new Set(), entries: [],
      };
      map.set(key, g);
    }
    g.totalQuantity += Number(r.quantity || 0);
    g.entryCount += 1;
    if (new Date(r.removed_at) > new Date(g.lastRemovedAt)) g.lastRemovedAt = r.removed_at;
    g.reasonSet.add(r.reason in REASON_LABEL ? r.reason : "other");
    g.entries.push(r);
  }
  for (const g of map.values()) {
    g.entries.sort((a, b) => new Date(b.removed_at) - new Date(a.removed_at));
    g.reasons = Array.from(g.reasonSet);
  }
  return Array.from(map.values());
}

const DATE_BUCKETS = [
  { id: "today",     label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "last7",     label: "Last 7 days" },
  { id: "older",     label: "Older" },
];

function bucketForDate(d) {
  const now = new Date();
  const startToday = new Date(now); startToday.setHours(0, 0, 0, 0);
  const startYesterday = new Date(startToday); startYesterday.setDate(startToday.getDate() - 1);
  const start7 = new Date(startToday); start7.setDate(startToday.getDate() - 7);
  if (d >= startToday)     return "today";
  if (d >= startYesterday) return "yesterday";
  if (d >= start7)         return "last7";
  return "older";
}

const SORTERS = {
  newest: (a, b) => new Date(b.lastRemovedAt) - new Date(a.lastRemovedAt),
  oldest: (a, b) => new Date(a.lastRemovedAt) - new Date(b.lastRemovedAt),
  units:  (a, b) => b.totalQuantity - a.totalQuantity,
  name:   (a, b) => (a.product_name || "").localeCompare(b.product_name || ""),
};
const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "units",  label: "Highest units removed" },
  { value: "name",   label: "Product name" },
];

function bucketProductGroups(groups, sortMode) {
  const buckets = { today: [], yesterday: [], last7: [], older: [] };
  for (const g of groups) buckets[bucketForDate(new Date(g.lastRemovedAt))].push(g);
  for (const k of Object.keys(buckets)) buckets[k].sort(SORTERS[sortMode] || SORTERS.newest);
  return DATE_BUCKETS.map((b) => ({
    id: b.id, label: b.label, groups: buckets[b.id],
    entryCount: buckets[b.id].reduce((a, g) => a + g.entryCount, 0),
    totalUnits: buckets[b.id].reduce((a, g) => a + g.totalQuantity, 0),
  }));
}

function computeSummary(rows) {
  let units = 0, expired = 0, near = 0, damaged = 0;
  for (const r of rows) {
    const q = Number(r.quantity || 0);
    units += q;
    const rk = r.reason in REASON_LABEL ? r.reason : "other";
    if (rk === "expired") expired += q;
    else if (rk === "near_expiration") near += q;
    else damaged += q;
  }
  return { units, entries: rows.length, expired, near, damaged };
}

function SummaryTile({ label, value, tone, icon: Icon, testid }) {
  const toneMap = {
    expired: "bg-status-expired-bg text-status-expired",
    urgent:  "bg-status-urgent-bg text-status-urgent",
    neutral: "bg-paper-sunk text-ink",
    brand:   "bg-brand-tint text-ink",
  };
  return (
    <Card variant="raised" className="p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="data-sku truncate">{label}</div>
        {Icon && (
          <div className={`h-6 w-6 rounded-chip flex items-center justify-center ${toneMap[tone] || toneMap.neutral}`}>
            <Icon size={12} strokeWidth={2.5} />
          </div>
        )}
      </div>
      <div className="font-display font-black text-2xl tabular-nums leading-none">{value}</div>
    </Card>
  );
}

function ReasonChip({ reasons }) {
  if (!reasons || reasons.length === 0) return null;
  const primary = reasons[0];
  return (
    <div className="flex items-center gap-1 shrink-0">
      <StatusChip tone={REASON_TONE[primary] || "neutral"}>{REASON_LABEL[primary] || primary}</StatusChip>
      {reasons.length > 1 && (
        <span className="font-mono text-[10px] text-ink-3 whitespace-nowrap">+{reasons.length - 1}</span>
      )}
    </div>
  );
}

function ProductGroupRow({ group, expanded, onToggle, operatorLookup }) {
  return (
    <Card variant="flat" className="bg-paper-2" data-testid={`adm-removal-group-${group.key}`}>
      <button
        type="button"
        onClick={() => onToggle(group.key)}
        className="w-full flex items-center gap-3 p-3 text-left active:bg-paper-sunk transition-colors"
        aria-expanded={expanded}
        data-testid={`adm-removal-group-toggle-${group.key}`}
      >
        <ProductImage src={group.product_image_url} alt={group.product_name} size="w-10 h-10" className="rounded-button overflow-hidden shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold text-[14px] leading-tight text-ink truncate">{group.product_name}</div>
          <div className="mt-0.5 font-mono text-[11px] text-ink-3 truncate">
            <span className="text-ink font-semibold tabular-nums">{group.totalQuantity}</span><span> units</span>
            <span className="opacity-60"> · </span>
            <span className="tabular-nums">{group.entryCount}</span><span> {group.entryCount === 1 ? "entry" : "entries"}</span>
            <span className="opacity-60"> · </span>
            <span>{formatTimeShort(group.lastRemovedAt)}</span>
          </div>
        </div>
        <ReasonChip reasons={group.reasons} />
        <ChevronRight size={16} className={`text-ink-3 shrink-0 transition-transform duration-150 ${expanded ? "rotate-90" : ""}`} />
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-border/40">
          <ul className="space-y-2">
            {group.entries.map((r) => {
              const rk = r.reason in REASON_LABEL ? r.reason : "other";
              return (
                <li key={r.id} className="flex items-start justify-between gap-3" data-testid={`adm-removal-detail-row-${r.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[12px] text-ink tabular-nums">
                      <span className="font-semibold">{r.quantity}</span><span className="opacity-50"> · </span>
                      <span>{formatDateTime(r.removed_at)}</span>
                    </div>
                    <div className="mt-0.5 font-mono text-[11px] text-ink-3 truncate">
                      {operatorLookup ? operatorLookup(r) : operatorLabel(r.removed_by)}
                      {r.note ? (<><span className="opacity-60"> · </span>{r.note}</>) : null}
                    </div>
                  </div>
                  <StatusChip tone={REASON_TONE[rk] || "neutral"}>{REASON_LABEL[rk] || rk}</StatusChip>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Card>
  );
}

function DateSection({ bucket, collapsed, onToggle, operatorLookup, productExpanded, onProductToggle }) {
  if (bucket.groups.length === 0) return null;
  return (
    <section className="mb-4" data-testid={`adm-removals-bucket-${bucket.id}`}>
      <button
        type="button"
        onClick={() => onToggle(bucket.id)}
        className="w-full flex items-center gap-2 mb-2 text-left"
        aria-expanded={!collapsed}
        data-testid={`adm-removals-bucket-toggle-${bucket.id}`}
      >
        <ChevronDown size={14} className={`text-ink-3 transition-transform duration-150 ${collapsed ? "-rotate-90" : ""}`} />
        <span className="font-display font-bold text-[12px] uppercase tracking-wider text-ink">{bucket.label}</span>
        <span className="font-mono text-[11px] text-ink-3">
          {bucket.entryCount} {bucket.entryCount === 1 ? "entry" : "entries"} · {bucket.totalUnits} units
        </span>
      </button>
      {!collapsed && (
        <div className="space-y-2">
          {bucket.groups.map((g) => (
            <ProductGroupRow
              key={g.key}
              group={g}
              expanded={productExpanded.has(g.key)}
              onToggle={onProductToggle}
              operatorLookup={operatorLookup}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default function AdminRemovalsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [productExpanded, setProductExpanded] = useState(() => new Set());
  const [sortMode, setSortMode] = useState("newest");
  const [collapsedBuckets, setCollapsedBuckets] = useState(() => new Set());

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/removals");
        setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error(formatApiErrorDetail(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredRows  = useMemo(() => applyFilters(items, filters), [items, filters]);
  const productGroups = useMemo(() => buildProductGroups(filteredRows), [filteredRows]);
  const dateBuckets   = useMemo(() => bucketProductGroups(productGroups, sortMode), [productGroups, sortMode]);
  const summary       = useMemo(() => computeSummary(filteredRows), [filteredRows]);
  const operatorLookup = useMemo(() => makeOperatorResolver(items), [items]);
  const operators = useMemo(() => {
    const seen = new Map();
    for (const r of items) {
      const id = r.removed_by || "";
      if (id && !seen.has(id)) seen.set(id, { id, label: operatorLookup(id) });
    }
    return Array.from(seen.values());
  }, [items, operatorLookup]);

  const toggleProduct = (key) => {
    setProductExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const toggleBucket = (id) => {
    setCollapsedBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const activeFilterCount =
    (filters.query ? 1 : 0) +
    (filters.datePreset !== "all" ? 1 : 0) +
    (filters.reasons.length > 0 ? 1 : 0) +
    (filters.operators.length > 0 ? 1 : 0);

  const hasAnyMatch = filteredRows.length > 0;

  return (
    <div className="pb-28" data-testid="admin-removals-page">
      <PageHeader eyebrow="Audit" title="Removal Log" />

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4" data-testid="adm-removals-summary-tiles">
        <SummaryTile label="Units removed"    value={summary.units.toLocaleString()}    tone="brand"   icon={Box}            testid="adm-removals-summary-units" />
        <SummaryTile label="Entries"          value={summary.entries.toLocaleString()}  tone="neutral" icon={Receipt}        testid="adm-removals-summary-entries" />
        <SummaryTile label="Expired"          value={summary.expired.toLocaleString()}  tone="expired" icon={Flame}          testid="adm-removals-summary-expired" />
        <SummaryTile label="Near expiration"  value={summary.near.toLocaleString()}     tone="urgent"  icon={TrendingDown}   testid="adm-removals-summary-near" />
        <SummaryTile label="Damaged / Other"  value={summary.damaged.toLocaleString()}  tone="neutral" icon={AlertTriangle}  testid="adm-removals-summary-other" />
      </div>

      {/* Search + filter */}
      <div className="flex items-center gap-2 mb-3">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="flex-1 h-11 px-3 bg-paper-2 rounded-button shadow-rest flex items-center gap-3 active:scale-[0.98] transition-all duration-150 text-left"
          data-testid="adm-removals-open-search"
        >
          <Search size={16} className="text-ink-3 shrink-0" />
          <span className="font-body text-sm text-ink-3 truncate">
            {filters.query ? filters.query : "Search by name, barcode, or reason"}
          </span>
          {filters.query && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setFilters((f) => ({ ...f, query: "" })); }}
              className="-mr-1 w-7 h-7 flex items-center justify-center text-ink-3 hover:text-ink"
              data-testid="adm-removals-clear-query"
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </button>
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className={`relative h-11 w-11 rounded-button flex items-center justify-center shadow-rest active:scale-[0.98] transition-all duration-150 ${
            activeFilterCount > 0 ? "bg-ink text-paper" : "bg-paper-2 text-ink-2"
          }`}
          data-testid="adm-removals-open-filters"
          aria-label="Open filters"
        >
          <SlidersHorizontal size={16} />
          {activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-chip bg-brand text-white font-mono text-[10px] flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Sort */}
      <div className="flex items-center gap-2 mb-4" data-testid="adm-removals-sort-row">
        <ArrowUpDown size={12} className="text-ink-3 shrink-0" />
        <label className="data-sku" htmlFor="adm-removals-sort">Sort</label>
        <div className="relative flex-1 max-w-[220px]">
          <select
            id="adm-removals-sort"
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
            className="w-full h-9 pl-3 pr-8 rounded-button bg-paper-2 shadow-rest font-mono text-[12px] uppercase tracking-wide appearance-none cursor-pointer focus:shadow-raised focus:outline-none transition-shadow"
            data-testid="adm-removals-sort-select"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238A8A8A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 10px center",
            }}
          >
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {loading && (
        <Card variant="raised" className="p-6"><div className="data-sku text-center">Loading removals…</div></Card>
      )}

      {!loading && !hasAnyMatch && (
        <EmptyState
          icon={<Receipt size={36} strokeWidth={1.5} />}
          title={activeFilterCount > 0 ? "No removals match these filters" : "No removals yet"}
          description={
            activeFilterCount > 0
              ? "Try clearing some filters or widening the date range."
              : "Removed items will show up here as the team processes shelf-life and damage."
          }
          action={
            activeFilterCount > 0 ? (
              <button
                type="button"
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="font-mono text-[11px] uppercase tracking-wider text-ink underline-offset-2 underline hover:no-underline"
                data-testid="adm-removals-clear-all"
              >
                Clear all filters
              </button>
            ) : null
          }
          data-testid="adm-removals-empty-state"
        />
      )}

      {!loading && hasAnyMatch && (
        <div data-testid="adm-removals-buckets-list">
          {dateBuckets.map((bucket) => (
            <DateSection
              key={bucket.id}
              bucket={bucket}
              collapsed={collapsedBuckets.has(bucket.id)}
              onToggle={toggleBucket}
              operatorLookup={operatorLookup}
              productExpanded={productExpanded}
              onProductToggle={toggleProduct}
            />
          ))}
        </div>
      )}

      <RemovalsFilterSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        initialFilters={filters}
        operators={operators}
        totalMatches={items.length}
        onApply={setFilters}
      />
    </div>
  );
}
